import { englishPairScore } from "@/lib/tool-kit/ciphers-misc/english-ngrams";

/**
 * The scytale as a transposition: wind a strip round a rod, write along the rod, unwind.
 *
 * A rod with `faces` letters round its circumference carries the message in `faces`
 * lines along its length. Written row by row, the message fills those lines; the strip
 * that was wound round the rod then reads the lines column by column — one letter from
 * each face, then the next position along the rod. Unwound, that reading order is the
 * cipher text.
 *
 * With n letters and `faces` lines, the rod is m = ⌈n ÷ faces⌉ positions long and
 * plain letter `row × m + column` becomes cipher letter `column × faces + row`. When n is
 * not a multiple of `faces` the last cells of the rod stay empty and are skipped in the
 * reading, so no padding is needed and decoding works from the length alone. A caller
 * that wants the strip filled can pad first (`padToRod`).
 *
 * Characters are code points. The caller decides beforehand (with `applyTextOptions`)
 * which spaces and punctuation belong to the message.
 */

export const MIN_FACES = 2;
export const MAX_FACES = 60;

/** Rod length (positions along it) for `length` letters on `faces` lines. */
export function rodLength(length: number, faces: number): number {
  return Math.max(1, Math.ceil(length / faces));
}

/** The plain-text index sitting at each cipher-text position, in reading order. */
function readingOrder(length: number, faces: number): number[] {
  const columns = rodLength(length, faces);
  const order: number[] = [];
  for (let column = 0; column < columns; column += 1) {
    for (let row = 0; row < faces; row += 1) {
      const index = row * columns + column;
      if (index < length) {
        order.push(index);
      }
    }
  }
  return order;
}

/** Fill the last turn of the strip so every face has a letter at every position. */
export function padToRod(text: string, faces: number, filler: string): string {
  const chars = [...text];
  const pad = [...filler].at(0);
  if (pad === undefined || chars.length === 0) {
    return text;
  }
  const missing = (faces - (chars.length % faces)) % faces;
  return chars.join("") + pad.repeat(missing);
}

export function scytaleEncode(text: string, faces: number): string {
  const chars = [...text];
  return readingOrder(chars.length, faces)
    .map((index) => chars[index])
    .join("");
}

export function scytaleDecode(text: string, faces: number): string {
  const chars = [...text];
  const plain: string[] = Array.from({ length: chars.length }, () => "");
  for (const [position, index] of readingOrder(chars.length, faces).entries()) {
    plain[index] = chars[position];
  }
  return plain.join("");
}

/**
 * The rod as a grid: `faces` rows by one column per position along the rod, holding the
 * plain text row by row, with `null` in the empty cells at the end.
 */
export function scytaleGrid(plain: string, faces: number): (string | null)[][] {
  const chars = [...plain];
  const columns = rodLength(chars.length, faces);
  return Array.from({ length: faces }, (_, row) =>
    Array.from({ length: columns }, (_, column) => {
      const index = row * columns + column;
      return index < chars.length ? chars[index] : null;
    })
  );
}

/** The cipher text cut into one group per turn of the strip: `faces` letters each. */
export function stripTurns(cipher: string, faces: number): string[] {
  const chars = [...cipher];
  const turns: string[] = [];
  for (let start = 0; start < chars.length; start += faces) {
    turns.push(chars.slice(start, start + faces).join(""));
  }
  return turns;
}

export interface ScytaleCandidate {
  faces: number;
  plain: string;
  /** English-likeness from common letter pairs and triples; higher is better. */
  score: number;
}

/**
 * Every rod size from 2 up to `maxFaces` (and at most one less than the length) that
 * reads differently, best-looking first. The score weights common English letter pairs and triples, so on a short
 * message the ranking is a hint — read the top few.
 */
export function scytaleCrack(
  cipher: string,
  maxFaces = MAX_FACES
): ScytaleCandidate[] {
  const length = [...cipher].length;
  const limit = Math.min(maxFaces, length - 1);
  const candidates: ScytaleCandidate[] = [];
  const seen = new Set<string>();
  for (let faces = MIN_FACES; faces <= limit; faces += 1) {
    const plain = scytaleDecode(cipher, faces);
    // Several large rods leave the same rod length and read identically; list the first.
    if (!seen.has(plain)) {
      seen.add(plain);
      candidates.push({ faces, plain, score: englishPairScore(plain) });
    }
  }
  return candidates.sort((a, b) => b.score - a.score || a.faces - b.faces);
}
